import { PageRoutes } from "./routes";
import {
  isSubscriptionDisabled,
  isTizen,
  isWebos,
  type Platform,
} from "../utils/platform";
import { DEFAULT_LANGUAGE } from "../models/language";

export const SCROLL_AMOUNT = 200;

export enum SettingsView {
  Settings = "Settings",
  General = "General Settings",
  ProfileInfo = "Account Info",
  LegalInformation = "Legal Information",
  CookieSettings = "Cookie Settings",
  CookiePolicy = "Cookie Policy",
  OpenSource = "Open Source Libraries",
  Privacy = "Privacy Policy",
  Tnc = "Terms & Conditions",
  Support = "Support & FAQs",
  Exit = "Exit",
  Subscription = "Subscription",
  StreamingProfile = "Streaming Profile",
}

export const SETTINGS_VIEW_TITLE_KEYS: Record<SettingsView, string> = {
  [SettingsView.Settings]: "Settings_Settings",
  [SettingsView.General]: "Settings_General Settings",
  [SettingsView.ProfileInfo]: "Settings_Account Info",
  [SettingsView.LegalInformation]: "Settings_Legal Information",
  [SettingsView.CookieSettings]: "Settings_Cookie Settings",
  [SettingsView.CookiePolicy]: "Settings_Cookie Policy",
  [SettingsView.OpenSource]: "Settings_Open Source Libraries",
  [SettingsView.Privacy]: "Settings_Privacy Policy",
  [SettingsView.Tnc]: "Settings_Terms & Conditions",
  [SettingsView.Support]: "Settings_Support & FAQs",
  [SettingsView.Exit]: "Settings_Exit",
  [SettingsView.Subscription]: "Settings_Subscription",
  [SettingsView.StreamingProfile]: "Settings_Streaming Profile",
};

const settingGroups = [
  [
    {
      icon: "icon-linear-setting-2",
      titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.General],
      settingsView: SettingsView.General,
      pageRoute: PageRoutes.General,
    },
    {
      icon: "icon-linear-user",
      titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.ProfileInfo],
      settingsView: SettingsView.ProfileInfo,
      pageRoute: PageRoutes.ProfileInfo,
    },
    {
      icon: "icon-linear-menu-wallet",
      titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Subscription],
      settingsView: SettingsView.Subscription,
      pageRoute: PageRoutes.Subscription,
    },
  ],
  [
    {
      icon: "icon-linear-security-safe",
      titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.LegalInformation],
      settingsView: SettingsView.LegalInformation,
      pageRoute: PageRoutes.LegalInformation,
    },
    {
      icon: "icon-security",
      titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.CookieSettings],
      settingsView: SettingsView.CookieSettings,
      pageRoute: PageRoutes.CookieSettings,
    },
  ],
  [
    {
      icon: "icon-linear-messages",
      titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Support],
      settingsView: SettingsView.Support,
      pageRoute: PageRoutes.Support,
    },
  ],
  [
    {
      icon: "icon-logout",
      titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Exit],
      settingsView: SettingsView.Exit,
      pageRoute: PageRoutes.Exit,
    },
  ],
];

export const getBaseSettingGroups = (isNonMainProfile = false) =>
  isSubscriptionDisabled || isNonMainProfile
    ? settingGroups.map((group) =>
        group.filter((item) => item.settingsView !== SettingsView.Subscription),
      )
    : settingGroups;

// Default groups are used by static consumers that do not have an active profile.
export const baseSettingGroups = getBaseSettingGroups();

export const REASONS_UNSUBSCRIBE = [
  { id: "Too expensive", labelKey: "PopupUnsubscribe_Too expensive" },
  { id: "Not using enough", labelKey: "PopupUnsubscribe_Not using enough" },
  { id: "Not enough games", labelKey: "PopupUnsubscribe_Not enough games" },
  {
    id: "Can not get steam to work",
    labelKey: "PopupUnsubscribe_Can not get steam to work",
  },
  { id: "Other", labelKey: "PopupUnsubscribe_Other" },
];

export const STORAGE_STATS_PINNED = "isStatsPinned";

export const LIMIT_USERNAME = 16;

export const FAQ_LINK =
  "https://play-onmo.helpshift.com/hc/en/5-onmo-1773744629/?han=1&hpn=1&p=web&hbhf=1";

// Landing site base URL (always ends with "/"), e.g. https://onmoplus.com/
export const LANDING_SITE_URL = (
  import.meta.env.VITE_LANDING_SIDE || "https://onmoplus.com/"
).replace(/\/*$/, "/");
// Landing site without protocol, used as display text, e.g. onmoplus.com/
const LANDING_SITE_DISPLAY = LANDING_SITE_URL.replace(/^https?:\/\//, "");

// <landing site>/data/<lang>/<doc>.html (see vite.config.ts) to avoid CORS,
// e.g. /data/es/privacy.html
const getLegalDocUrl = (doc: string, language: string = DEFAULT_LANGUAGE) =>
  `${LANDING_SITE_URL}data/${language}/${doc}.html`;
export const getTncDocUrl = (language?: string) =>
  getLegalDocUrl("terms", language);
export const getPrivacyDocUrl = (language?: string) =>
  getLegalDocUrl("privacy", language);
export const getCookiePolicyDocUrl = (language?: string) =>
  getLegalDocUrl("cookie", language);

export const TNC_DOC_URL = getTncDocUrl();
export const PRIVACY_DOC_URL = getPrivacyDocUrl();

export const OPEN_SOURCE_DOC_URL = `${LANDING_SITE_URL}data/open-source.html`;

// TV (QR code) legal document links
export const TNC_QR_URL = `${LANDING_SITE_URL}terms-and-conditions`;
export const PRIVACY_QR_URL = `${LANDING_SITE_URL}privacy-policy`;
export const OPEN_SOURCE_QR_URL = `${LANDING_SITE_URL}open-source`;
export const FAQ_LINK_URL = "https://help.onmoplus.com/";

export interface ILegalGroupItem {
  titleKey: string;
  link: string;
  qrUrl: string;
  docUrl: string;
  settingsView?: SettingsView;
  pageRoute?: PageRoutes;
}

export const LEGAL_DETAIL_VIEWS: SettingsView[] = [
  SettingsView.Tnc,
  SettingsView.Privacy,
  SettingsView.OpenSource,
];
export const GENERAL_DETAIL_VIEWS: SettingsView[] = [
  SettingsView.StreamingProfile,
];
export const COOKIE_DETAIL_VIEWS: SettingsView[] = [SettingsView.CookiePolicy];

export const resolveMenuView = (view: SettingsView): SettingsView => {
  if (LEGAL_DETAIL_VIEWS.includes(view)) return SettingsView.LegalInformation;
  if (GENERAL_DETAIL_VIEWS.includes(view)) return SettingsView.General;
  if (COOKIE_DETAIL_VIEWS.includes(view)) return SettingsView.CookieSettings;
  return view;
};

export const SETTINGS_VIEW_ROUTES: Partial<Record<SettingsView, PageRoutes>> = {
  [SettingsView.General]: PageRoutes.General,
  [SettingsView.ProfileInfo]: PageRoutes.ProfileInfo,
  [SettingsView.Subscription]: PageRoutes.Subscription,
  [SettingsView.LegalInformation]: PageRoutes.LegalInformation,
  [SettingsView.CookieSettings]: PageRoutes.CookieSettings,
  [SettingsView.Support]: PageRoutes.Support,
  [SettingsView.Exit]: PageRoutes.Exit,
  [SettingsView.Privacy]: PageRoutes.Privacy,
  [SettingsView.Tnc]: PageRoutes.Tnc,
  [SettingsView.OpenSource]: PageRoutes.OpenSource,
  [SettingsView.CookiePolicy]: PageRoutes.CookiePolicy,
};

export const LEGAL_GROUP: ILegalGroupItem[] = [
  {
    titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Tnc],
    link: `${LANDING_SITE_DISPLAY}terms-and-conditions`,
    qrUrl: TNC_QR_URL,
    docUrl: TNC_DOC_URL,
    settingsView: SettingsView.Tnc,
    pageRoute: PageRoutes.Tnc,
  },
  {
    titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Privacy],
    link: `${LANDING_SITE_DISPLAY}privacy-policy`,
    qrUrl: PRIVACY_QR_URL,
    docUrl: PRIVACY_DOC_URL,
    settingsView: SettingsView.Privacy,
    pageRoute: PageRoutes.Privacy,
  },
  {
    titleKey: SETTINGS_VIEW_TITLE_KEYS[SettingsView.OpenSource],
    link: `${LANDING_SITE_DISPLAY}open-source`,
    qrUrl: OPEN_SOURCE_QR_URL,
    docUrl: OPEN_SOURCE_DOC_URL,
    settingsView: SettingsView.OpenSource,
    pageRoute: PageRoutes.OpenSource,
  },
];

export interface IOpenSourceLibrary {
  slug: string;
  name: string;
  license: string;
  platforms: Platform[];
}

export const OPEN_SOURCE_DESCRIPTION_KEY =
  "OpenSourceLibraries_This app is built with the help of the following open source libraries.";

const APP_ROOT_URL = globalThis.location.href
  .split(/[?#]/)[0]
  .replace(/[^/]*$/, "");

export const OPEN_SOURCE_DOC_BASE =
  isWebos || isTizen
    ? `${APP_ROOT_URL}legal/open-source/`
    : `/legal/open-source/`;

export const OPEN_SOURCE_INDEX_URL = `${OPEN_SOURCE_DOC_BASE}index.json`;
export const getOpenSourceDocUrl = (slug: string) =>
  `${OPEN_SOURCE_DOC_BASE}${slug}.html`;
